/**
 * Human-readable labels for the fixed report-dropdown keys.
 *
 * The keys are the `span[data-editable]` classes inside the clinic report
 * templates — technical, historically grown, and sometimes misleading
 * (`la-no` actually grades aortic regurgitation/stenosis severity). This map
 * gives admins a clinical name; the raw key stays visible as a secondary
 * line so it can still be matched against the template HTML.
 *
 * Labels were derived from each span's sentence context in the seeded
 * ECHO / Stress Echo templates. Keys not listed here (e.g. from a clinic's
 * custom docx template) fall back to a prettified form of the key itself.
 */
export const DROPDOWN_KEY_LABELS: Record<string, string> = {
  'study-quality': 'Study quality',
  'sinus-rhythm': 'Heart rhythm',
  'lv-normal-one': 'LV size',
  'lv-normal-second': 'LV systolic function',
  'lv-biplane': 'EF measurement method',
  'lv-normal-third': 'LV wall thickness',
  'lv-normal-fourth': 'LV diastolic function',
  'lv-normal-fifth': 'LV filling pressures',
  'rv-normal': 'RV size',
  'rv-systolic-function': 'RV systolic function',
  'la-normal': 'LA size',
  'la-septal-defect': 'Atrial septal defect / PFO statement',
  'ra-normal': 'RA size',
  'aorta-normal': 'Aortic root size',
  'aorta-ascending': 'Ascending aorta size',
  'av-valve': 'Aortic valve structure',
  'av-trileaflet': 'Aortic valve morphology',
  'lq-mm-valve': 'Aortic valve prosthesis statement',
  'la-no': 'Aortic regurgitation / stenosis severity',
  'la-effective': 'Aortic valve haemodynamics statement',
  'mv-normal': 'Mitral valve structure',
  'mv-calcification': 'Mitral leaflet finding',
  'mv-trivial': 'Mitral regurgitation severity',
  'mv-no': 'Mitral stenosis severity',
  'mv-mean-pressure': 'Mitral gradient & valve area statement',
  'tv-trivial': 'Tricuspid regurgitation severity',
  'tv-mmhg': 'Assumed right atrial pressure (mmHg)',
  'pv-trivial': 'Pulmonary regurgitation severity',
  physiological: 'Pericardial effusion',
  recommendations: 'Recommendations',

  // Stress echo template. The baseline-echo phrases in that report deliberately
  // reuse the ECHO keys above (lv-normal-one, lv-normal-second, rv-normal,
  // sinus-rhythm) so a clinic's existing lists apply with nothing to configure —
  // only the stress-specific slots below are new.
  'stress-ecg-resting': 'Resting ECG interpretation',
  'stress-rwma-baseline': 'Baseline regional wall motion',
  'stress-protocol': 'Exercise protocol',
  'stress-bp-response': 'Blood pressure response',
  'stress-termination': 'Reason test was terminated',
  'stress-symptoms': 'Patient symptoms',
  'stress-ecg-st': 'ST segment response',
  'stress-ecg-twave': 'T wave response',
  'stress-arrhythmia': 'Arrhythmias during test',
  'stress-rwma-post': 'Post-stress regional wall motion',
  'stress-cad-probability': 'Probability of obstructive CAD',
  'stress-result': 'Stress echo result',
  'stress-exercise-capacity': 'Exercise capacity',
  // Whether the patient reached their age-predicted maximum heart rate. The
  // report states "Target HR achieved" by default and the sonographer changes
  // the wording when it was not — Dr Shashi, 31 Aug: "base can be 'target Hr
  // achieved' - as if it is not achieved, sonographers can change the wording."
  'stress-target-hr-status': 'Target heart rate achieved',
};

/** Friendly label for a key; unknown keys are prettified (kebab → Title Case). */
export const labelForDropdownKey = (key: string): string => {
  if (DROPDOWN_KEY_LABELS[key]) {
    return DROPDOWN_KEY_LABELS[key];
  }
  return key
    .split('-')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};
